import { useEffect, useState } from "react";
import { api } from "./api";
import type { Me } from "../types";

// The signed-in user (GET /api/me), including the resolved DevHub CMS role/team.
// Several CMS screens gate affordances on the role, so they share this hook.
//
// `org` names which org the CMS role is resolved FOR. A role is per org
// (devhub_roles is keyed `<org>/<sub>`), so a caller that gates on `me.role` while
// looking at one org's CMS has to ask about that org — unscoped, the server answers
// with Incode's, and a surface would show one org's authority over another's data.
// Omitted keeps the unscoped answer, which is what every caller that only needs the
// identity (name, email, admin bit) means.
export function useMe(org?: string): Me | null {
  const [me, setMe] = useState<Me | null>(null);
  useEffect(() => {
    // Guarded, now that the answer depends on an argument that can change: two
    // orgs' requests are in flight across a switch and they can land in either
    // order, so an unguarded `setMe` could leave the role of the org you just left
    // deciding what you may do in the one you are now looking at. Whichever
    // request was superseded drops its answer.
    let live = true;
    void api.getMe(org).then(
      (next) => { if (live) setMe(next); },
      () => { if (live) setMe(null); },
    );
    return () => { live = false; };
  }, [org]);
  return me;
}

// App-level Omni Hub admin — defined in ./appAdmin so a caller that only needs the
// predicate isn't made to load React and the auth client for it (see that file).
// Re-exported here because this is where it has always been imported from.
export { isAppAdmin } from "./appAdmin";
